/**
 * ExtractionConfirmCard — shows mock-extracted fields after a document upload.
 *
 * Displayed inline in the chat thread after a successful upload. The user
 * reviews the auto-extracted fields and confirms ("Looks correct →") or
 * dismisses ("Skip for now"). No backend writes in Phase 1 — purely a UI
 * confirmation step. Real OCR will replace the mock values in a later phase.
 *
 * Pure display component: the parent owns state and mutation calls.
 */
import { DOC_TYPE_LABELS } from "@/components/workspace/ChatUploadZone";

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  documentType: string;
  fileName: string;
  fields: { label: string; value: string }[];
  onConfirm: () => void;
  onSkip: () => void;
}

export function ExtractionConfirmCard({ documentType, fileName, fields, onConfirm, onSkip }: Props) {
  const docLabel = DOC_TYPE_LABELS[documentType] ?? documentType;

  return (
    <div className="space-y-1">
      {/* Assistant bubble with extracted data */}
      <div className="flex gap-2.5">
        <AssistantIcon />
        <div className="bg-slate-50 border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3.5 max-w-[85%] min-w-0">
          <p className="text-sm text-slate-700 leading-relaxed mb-3">
            I found the following details on your{" "}
            <span className="font-semibold">{docLabel}</span>. Do they look right?
          </p>

          {/* Extracted fields table */}
          <div className="space-y-2 mb-4">
            {fields.map((f) => (
              <div key={f.label} className="flex gap-2 text-xs">
                <span className="text-slate-400 shrink-0 w-36 leading-relaxed">{f.label}</span>
                <span className="text-slate-800 font-medium leading-relaxed break-words min-w-0">{f.value}</span>
              </div>
            ))}
          </div>

          {/* Phase 1 disclaimer */}
          <p className="text-[10px] text-slate-400 mb-3.5 leading-snug italic">
            Demo values — real document scanning is coming soon.
          </p>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={onConfirm}
              className="flex-1 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl py-2.5 transition-colors"
            >
              Looks correct →
            </button>
            <button
              onClick={onSkip}
              className="flex-1 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 active:bg-slate-300 rounded-xl py-2.5 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>

      {/* File name hint */}
      <p className="text-[10px] text-slate-400 pl-9 pb-1">
        Uploaded: <span className="font-medium text-slate-500">{fileName}</span>
      </p>
    </div>
  );
}

// ─── Icon helper ─────────────────────────────────────────────────────────────

function AssistantIcon() {
  return (
    <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center shrink-0 mt-0.5">
      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
      </svg>
    </div>
  );
}
